import { useEffect, useMemo, useState } from 'react'
import { useSeoMeta } from '@unhead/react'
import type { CalcInputs, LiveRateOverrides } from '@/lib/pricing'
import { calcAll } from '@/lib/pricing'
import { useLivePricing } from '@/hooks/useLivePricing'
import { readUrlInputs, pushInputsToUrl } from '@/lib/shareUrl'
import Header from '@/components/Header'
import Hero from '@/components/Hero'
import CalculatorPanel from '@/components/CalculatorPanel'
import CostMatrix from '@/components/CostMatrix'
import CostBreakdownChart from '@/components/CostBreakdownChart'
import RecommendationCard from '@/components/RecommendationCard'
import SeoHub from '@/components/SeoHub'
import CtaBlock from '@/components/CtaBlock'
import SeoChips from '@/components/SeoChips'
import Footer from '@/components/Footer'
import ShareBar from '@/components/ShareBar'

const DEFAULTS: CalcInputs = {
  cpuCores: 16,
  ramGb: 64,
  computeHours: 730,
  storageGb: 2000,
  storageTier: 'standard',
  egressGb: 5000,
  invocations: 2_000_000,
}

function LiveBadge({
  loading,
  error,
  refreshedAt,
  cached,
  refetch,
  liveCount,
}: {
  loading: boolean
  error: string | null
  refreshedAt: string | null
  cached: boolean
  refetch: () => void
  liveCount: number
}) {
  const fmtTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch {
      return iso
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/50 text-[11px] font-mono text-slate-400">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
        Fetching live rates…
      </div>
    )
  }

  if (error || liveCount === 0) {
    return (
      <button
        onClick={refetch}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/60 border border-slate-700/50 hover:border-slate-600 text-[11px] font-mono text-slate-500 hover:text-slate-300 transition-colors"
        title="Using static baseline rates — click to retry live fetch"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-500" />
        Static rates · retry
      </button>
    )
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-950/40 border border-emerald-800/40 text-[11px] font-mono text-emerald-400">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      Live rates · {liveCount}/4 providers
      {refreshedAt && (
        <span className="text-emerald-600 ml-1">
          · updated {fmtTime(refreshedAt)}{cached ? ' (cached)' : ''}
        </span>
      )}
    </div>
  )
}

function Home() {
  const [inputs, setInputs] = useState<CalcInputs>(() => readUrlInputs() ?? DEFAULTS)

  // Keep URL in sync whenever sliders change (replaceState — no extra history entry)
  useEffect(() => {
    pushInputsToUrl(inputs)
  }, [inputs])

  // ── Live pricing hook ────────────────────────────────────────────────────
  const { rates: liveRates, loading, error, refreshedAt, cached, refetch } = useLivePricing()

  // Build the override map — only include providers where live:true
  const overrides: LiveRateOverrides = useMemo(() => {
    const result: LiveRateOverrides = {}
    for (const [key, row] of Object.entries(liveRates)) {
      if (row?.live) {
        result[key as keyof LiveRateOverrides] = {
          cpuHour:     row.cpuHour,
          ramHour:     row.ramHour,
          storage:     row.storage,
          egress:      row.egress,
          invocations: row.invocations,
        }
      }
    }
    return result
  }, [liveRates])

  const liveCount = Object.values(liveRates).filter(r => r?.live).length

  // Recalculate whenever inputs OR live rates change
  const results = useMemo(() => calcAll(inputs, overrides), [inputs, overrides])

  useSeoMeta({
    title: 'Cloud Pricing Calculator — AWS, Azure, GCP & OCI Costs',
    description: 'Free multi-cloud pricing calculator. Compare AWS, Azure, Google Cloud & Oracle OCI infrastructure costs instantly. EC2, Lambda, BigQuery & egress estimates.',
    ogTitle: 'Cloud Pricing Calculator — Compare AWS, Azure, GCP & OCI',
    ogDescription: 'Instantly benchmark AWS, Azure, GCP and OCI costs across compute, storage, network and serverless.',
    ogImage: 'https://www.cloudcalculators.com/og-image.jpg',
    ogUrl: 'https://www.cloudcalculators.com/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Cloud Pricing Calculator — AWS, Azure, GCP & OCI',
    twitterDescription: 'Compare multi-cloud infrastructure costs in seconds.',
    twitterImage: 'https://www.cloudcalculators.com/og-image.jpg',
  })

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-200 selection:bg-cyan-500/30 selection:text-cyan-100">
      <Header />
      <Hero results={results} />

      <main id="calculator" className="max-w-7xl mx-auto px-5 md:px-8 pb-16">
        {/* Live pricing status bar */}
        <div className="flex items-center justify-between mb-6 pt-2">
          <p className="text-[11px] font-mono text-slate-500 hidden sm:block">
            Rates: AWS · Azure <span className="text-slate-600">(live)</span>{' '}
            + GCP · Oracle <span className="text-slate-600">(baseline)</span>
          </p>
          <LiveBadge
            loading={loading}
            error={error}
            refreshedAt={refreshedAt}
            cached={cached}
            refetch={refetch}
            liveCount={liveCount}
          />
        </div>

        {/* Share / Export bar */}
        <div className="mb-5">
          <ShareBar inputs={inputs} results={results} />
        </div>

        {/* Calculator + Dashboard grid */}
        <section id="dashboard" className="grid lg:grid-cols-12 gap-5 lg:gap-6">
          <div className="lg:col-span-5 lg:sticky lg:top-20 lg:self-start">
            <CalculatorPanel inputs={inputs} onChange={setInputs} />
          </div>
          <div className="lg:col-span-7 space-y-5 lg:space-y-6">
            <RecommendationCard results={results} />
            <CostMatrix results={results} />
            <CostBreakdownChart results={results} />
          </div>
        </section>

        {/* Long-tail SEO chips block */}
        <section className="mt-14">
          <div className="flex items-end justify-between mb-4">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Coverage</span>
              <h3 className="text-lg font-bold text-slate-100 tracking-tight mt-0.5">Niche calculators included</h3>
            </div>
            <span className="text-[11px] font-mono text-slate-500 hidden sm:block">10+ targeted SKUs</span>
          </div>
          <SeoChips />
        </section>

        {/* SEO Strategy Hub */}
        <section id="hub" className="mt-14">
          <div className="flex items-end justify-between mb-4">
            <div>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Knowledge Base</span>
              <h3 className="text-lg font-bold text-slate-100 tracking-tight mt-0.5">Cloud cost optimization guides</h3>
            </div>
          </div>
          <SeoHub />
        </section>

        {/* CTA */}
        <section id="cta" className="mt-14">
          <CtaBlock />
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Home
